import { getSiteCustomFieldDefaults } from "@fieldbook/common/utils/subformFields"
import { useAuth } from "@/hooks/useAuth"
import { PhotoInput } from "@/lib/queryClient/PartialSuccessError"
import { backupStagedPhotosForCreate } from "@/lib/drafts/backupStagedPhotosForCreate"
import { useCreateSurveySite } from "@/hooks/useCreateSurveySite"
import { useSurveySite } from "@/hooks/useSurveySite"
import { usePlannedSiteById } from "@/hooks/usePlannedSiteById"
import type { PlannedSite } from "@/hooks/usePlannedSitesByProject"
import {
  getFormFieldComponent,
  getSchema,
  SiteFormDataInput,
  type SiteFormContext,
  transformSurveySiteData,
} from "@fieldbook/common/schemas/sites"
import { RecordersSection } from "@fieldbook/common/components"
import { useNvisSpeciesResolver } from "@fieldbook/common/hooks"
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm, type FieldErrors } from "react-hook-form"
import * as z from "zod"

import {
  LocationSelector,
  LocationSelectorProps,
  LocationShapes,
} from "@/components/locations/LocationSelector"
import { PlannedSiteMarker } from "@/components/locations/maps/PlannedSiteMarker"
import { PhotosForm } from "@/components/photos/PhotosForm"
import { FormExitGuard } from "@/components/common/FormExitGuard"

import {
  PhotoChangesProvider,
  usePhotoChanges,
} from "@/components/photos/PhotosFormProvider"
import { useProjectData } from "@/contexts/ProjectData"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"
import { Button } from "@fieldbook/ui/button"
import { toast } from "@fieldbook/ui/toaster"
import { Input } from "@fieldbook/ui/input"
import { Label } from "@fieldbook/ui/label"
import { Spinner } from "@fieldbook/ui/spinner"
import { Textarea } from "@fieldbook/ui/textarea"
import { MapPin } from "lucide-react"
import { prepareSiteDefaultValues } from "./data/sites/-utils"
import { linkSurveySiteToPlannedSite } from "@/lib/plannedSites"
import { completePlannedSiteAssignment } from "@/lib/plannedSiteAssignments"
import { parsePostGISPoint } from "@fieldbook/common/utils"
import { useGeoLocation } from "@/contexts/GeoLocation"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@fieldbook/ui/alert-dialog"
import type { SiteType, SurveySite } from "@fieldbook/common/types"
import { useDebounce } from "@uidotdev/usehooks"
import posthog from "posthog-js"
import { getLocationError } from "@/lib/siteForm/errors"
import {
  useDraftRestore,
  useDraftRestoredNotice,
  useFormDraft,
} from "@/hooks/useFormDraft"
import { draftPhotoToStaged } from "@/lib/drafts/draftPhotos"
import { buildResumeKey, type FormDraft } from "@/lib/drafts/types"
import { captureFormSubmitBlocked } from "@/lib/siteForm/analytics"
import { useHasUnsavedChanges } from "@/hooks/useHasUnsavedChanges"
import { loadProjectWithSiteType } from "@/lib/powersync/projectLoader"
import { saveDraftForExit } from "@/lib/drafts/saveDraftForExit"

// When recording from a planned-site task, prompt for confirmation if the
// user's current location is further than this (meters) from the planned
// location. Recording nearby can be legitimate, so this only warns.
const FAR_FROM_PLANNED_THRESHOLD_M = 100

/** The site form keeps nothing outside react-hook-form worth restoring. */
type SiteDraft = FormDraft<SiteFormDataInput, null>

const siteSearchSchema = z.object({
  siteType: z.string(),
  duplicateFromId: z.string().optional(),
  // Mission Control: when recording a survey_site from a planned-site task,
  // the task's planned site id is passed through so the form can link the
  // two on save (and warn if the user is far from the planned location).
  // The assignment id (for an assigned-to-me task) is passed so the
  // assignment can be marked completed on save.
  plannedSiteId: z.string().optional(),
  assignmentId: z.string().optional(),
  draftId: z.string().uuid().optional(),
})

export const Route = createFileRoute("/_private/projects/$id/sites/new")({
  component: NewSiteRoute,
  validateSearch: (search) => siteSearchSchema.parse(search),
  // Resolve the project row and site type before the form renders: useForm
  // applies defaultValues only at mount, so mounting before the site type
  // is known froze location_type/type/custom-fields at their placeholders
  // (the "form has values but won't validate" bug). The loader gates
  // rendering; pendingComponent is the spinner while it waits — including
  // on cold-start deep links where the row hasn't synced yet.
  loaderDeps: ({ search }) => ({ siteTypeName: search.siteType }),
  loader: async ({ params, deps }) =>
    loadProjectWithSiteType(params.id, deps.siteTypeName),
  pendingComponent: () => (
    <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
      <Spinner />
    </div>
  ),
})

function NewSiteRoute() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId } = Route.useParams()
  const {
    duplicateFromId,
    plannedSiteId,
    siteType: siteTypeName,
  } = Route.useSearch()

  // The project row and site type are resolved by the route loader before
  // this component renders (pendingComponent shows while it waits). A
  // missing project here means the loader's wait budget expired before the
  // row synced; a missing site type means the project has no site type
  // with the requested name.
  const { project, siteType } = Route.useLoaderData()

  const { data: duplicateFrom, isPending: isDuplicatePending } = useSurveySite({
    siteId: duplicateFromId,
    projectId,
  })

  // Look up the planned site (local SQLite via PowerSync) so the form can
  // prefill its location and link back on save.
  const { data: plannedSite, isLoading: plannedSitesLoading } =
    usePlannedSiteById(plannedSiteId)

  const navigate = useNavigate()

  if (duplicateFromId && isDuplicatePending) {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  // Wait for the planned site to load before mounting the form so its
  // location can seed the form's default values at init.
  if (plannedSiteId && plannedSitesLoading && !plannedSite) {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="bg-background absolute inset-0 z-20 p-4">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-bold text-red-600">
            Couldn't load project
          </h1>
          <p className="mb-4 text-gray-600">
            The project data couldn't be loaded. Check your connection and try
            again.
          </p>
          <Button
            onClick={() =>
              navigate({ to: "/projects/$id", params: { id: projectId } })
            }
          >
            Back to Project
          </Button>
        </div>
      </div>
    )
  }

  if (!siteType) {
    return (
      <div className="bg-background absolute inset-0 z-20 p-4">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-bold text-red-600">
            Site Type Not Found
          </h1>
          <p className="mb-4 text-gray-600">
            The requested site type "{siteTypeName}" is not available for this
            project.
          </p>
          <Button
            onClick={() =>
              navigate({ to: "/projects/$id", params: { id: projectId } })
            }
          >
            Back to Project
          </Button>
        </div>
      </div>
    )
  }

  return (
    <NewSiteFormWithDraft
      siteType={siteType}
      duplicateFrom={duplicateFromId ? (duplicateFrom ?? undefined) : undefined}
      plannedSite={plannedSite ?? undefined}
    />
  )
}

/**
 * Resolves any autosaved draft before the form mounts. react-hook-form freezes
 * its defaults at mount, so this has to settle first — hence the separate
 * component rather than a branch inside AddSite.
 */
function NewSiteFormWithDraft({
  siteType,
  duplicateFrom,
  plannedSite,
}: {
  siteType: SiteType
  duplicateFrom?: SurveySite
  plannedSite?: PlannedSite
}) {
  const { id: projectId } = Route.useParams()
  const { user } = useAuth()
  const { duplicateFromId, plannedSiteId, assignmentId, draftId } =
    Route.useSearch()

  const resumeKey = buildResumeKey({
    kind: "site",
    projectId,
    mode: "new",
    siteTypeName: siteType.name,
    duplicateFromId,
    plannedSiteId,
    assignmentId,
  })

  const restore = useDraftRestore<SiteFormDataInput, null>(
    resumeKey,
    user?.id ?? null,
    draftId,
  )

  if (restore.status === "pending") {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <PhotoChangesProvider
      initialPhotos={[]}
      initialStagedPhotos={restore.draft?.photos.map(draftPhotoToStaged)}
    >
      <AddSite
        siteType={siteType}
        duplicateFrom={duplicateFrom}
        plannedSite={plannedSite}
        resumeKey={resumeKey}
        restoredDraft={restore.draft}
      />
    </PhotoChangesProvider>
  )
}

// const useFormDebug = (
//   form: UseFormReturn<SiteFormDataInput>,
//   schema: z.ZodType<SiteFormDataInput>,
// ) => {
//   useEffect(() => {
//     const subscription = form.watch((data) => {
//       const result = schema.safeParse(data)
//       console.log("Form data:", data)
//       console.log("Is valid:", form.formState.isValid)
//       console.log(
//         "Zod result ?:",
//         result.success ? "valid" : result.error.flatten(),
//       )
//       console.log({ result })
//     })
//     return () => subscription.unsubscribe()
//   }, [form, schema])
// }

function AddSite({
  siteType,
  duplicateFrom,
  plannedSite,
  resumeKey,
  restoredDraft,
}: {
  siteType: SiteType
  duplicateFrom?: SurveySite
  plannedSite?: PlannedSite
  resumeKey: string
  restoredDraft: SiteDraft | null
}) {
  const { id: projectId } = Route.useParams()

  const { assignmentId, draftId } = Route.useSearch()

  const {
    data: { project },
  } = useProjectData()
  const { user, org } = useAuth()
  const navigate = useNavigate()
  // Adopting a resumed draft's id continues that site rather than starting a
  // second one beside it, and keeps the uploads its staged photos were already
  // recorded against pointing at the right row.
  const siteIdRef = useRef<string>(restoredDraft?.id ?? crypto.randomUUID())

  const createSurveySite = useCreateSurveySite()

  const [photoChanges] = usePhotoChanges()

  const siteLocationType =
    siteType.site_location_type.toLocaleLowerCase() as SiteFormDataInput["location_type"]
  const schema = useMemo(() => getSchema(siteType), [siteType])

  const location_property =
    `location_${siteLocationType}` as keyof SiteFormDataInput

  const duplicateDefaults = useMemo(() => {
    if (!duplicateFrom) return null
    const defaults = prepareSiteDefaultValues(duplicateFrom)

    // Blank out NVIS vegetation descriptions when duplicating. The new site
    // starts with no observations, and an NVIS description that was derived
    // from the original site's observations would now be referring to taxa
    // that aren't present here — leaving it in place would either render
    // them as "Unknown species" or, worse, suggest the new site has
    // observations it doesn't. The user can regenerate from the detail page
    // once they've recorded observations at the new site.
    const customFieldsClone: Record<string, unknown> = {
      ...getSiteCustomFieldDefaults(siteType?.custom_fields),
      ...(defaults.custom_fields ?? {}),
    }
    for (const fieldDef of siteType?.custom_fields ?? []) {
      if (
        fieldDef.type === "subform" &&
        Array.isArray(customFieldsClone[fieldDef.name])
      ) {
        customFieldsClone[fieldDef.name] = (
          customFieldsClone[fieldDef.name] as Record<string, unknown>[]
        ).map((row) => ({ ...row, _row_id: crypto.randomUUID() }))
      }
      if (fieldDef.type === "nvis_vegetation_description") {
        delete customFieldsClone[fieldDef.name]
      }
    }

    // Clear fields that should be unique to the new site
    return {
      ...defaults,
      custom_fields: customFieldsClone as typeof defaults.custom_fields,
      name: undefined,
      location_point: undefined,
      location_line: undefined,
      location_polygon: undefined,
      created_by: user?.id ?? "No user",
      recorders: user?.id ? [user.id] : [],
    }
  }, [duplicateFrom, user?.id, siteType])

  // Typed as partial on purpose: a draft written before a custom field was
  // added has no value for it, and the computed defaults below must still
  // supply one rather than being blanked out by an absent key.
  const restoredValues: Partial<SiteFormDataInput> = restoredDraft?.values ?? {}

  const form = useForm<SiteFormDataInput>({
    defaultValues: {
      location_type: siteLocationType,
      project_id: projectId,
      created_by: user?.id ?? "No user",
      type: siteType.name,
      recorders: user?.id ? [user.id] : [],
      custom_fields: getSiteCustomFieldDefaults(siteType?.custom_fields),
      // Pre-seed the name from the planned site when recording from a task.
      // (duplicateDefaults, when present, clears it — the two flows are
      // mutually exclusive.)
      name: plannedSite?.name ?? undefined,
      ...duplicateDefaults,
      ...restoredValues,
    },
    resolver: standardSchemaResolver(schema),
    mode: "all",
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    watch,
    setValue,
    setError,
    clearErrors,
  } = form

  // DEBUG section
  const data = watch()
  const debouncedData = useDebounce(data, 500)
  const dataKey = useMemo(() => JSON.stringify(debouncedData), [debouncedData])
  useEffect(() => {
    posthog.capture("site_form_data_entry", {
      formData: debouncedData,
      parse: schema.safeParse(debouncedData),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, schema])
  // End debug section

  const draft = useFormDraft({
    resumeKey,
    kind: "site",
    projectId,
    ownerId: user?.id ?? null,
    entityId: siteIdRef.current,
    label: watch("name")?.trim() || "Unnamed survey site",
    form,
    extras: null,
    photos: photoChanges.add,
    existingPhotos: photoChanges.keep,
    restoredDraft,
    // Submit flushes the draft while isSubmitting, so keep writes enabled.
    enabled: Boolean(user),
  })

  useDraftRestoredNotice(restoredDraft, "site")

  const [descriptionFocus, setDescriptionFocus] = useState(false)
  const [showLocationSelector, setShowLocationSelector] = useState(false)

  // New sites never carry an NVIS description into the form: when duplicating
  // we blank the NVIS fields above, and otherwise there's nothing to render.
  // The resolver here is supplied with empty lookups for the same reason —
  // there's nothing for it to resolve against, and threading the project's
  // species/collections through to the form just to satisfy a context shape
  // that won't be used would be misleading.
  const { resolveDescription, resolveObservationInputs } =
    useNvisSpeciesResolver({ species: [], collections: [] })
  const siteContext = useMemo<SiteFormContext>(
    () => ({
      observations: [],
      observationFieldDefs: project?.observation_custom_fields ?? [],
      resolveNvisDescription: resolveDescription,
      resolveNvisObservationInputs: resolveObservationInputs,
    }),
    [
      project?.observation_custom_fields,
      resolveDescription,
      resolveObservationInputs,
    ],
  )

  const { getDistanceKm, location } = useGeoLocation()

  // Parsed planned-site coordinates, shared by the far-location warning and
  // the location-selector marker.
  const plannedSiteCoord = useMemo(() => {
    if (!plannedSite) return null
    try {
      return parsePostGISPoint(plannedSite.location_point)
    } catch {
      return null
    }
  }, [plannedSite])

  // When recording from a planned-site task, warn up front if the user's
  // current location is far from the planned location — it may mean they're
  // at the wrong spot. The check runs once, as soon as we have the planned
  // site and a GPS fix; recording nearby can be legitimate, so the modal
  // only warns and lets them continue or leave.
  const [farDistanceMeters, setFarDistanceMeters] = useState<number | null>(
    null,
  )
  const warnedFarRef = useRef(false)
  useEffect(() => {
    if (!plannedSiteCoord) return
    if (warnedFarRef.current) return
    const km = getDistanceKm({
      latitude: plannedSiteCoord.latitude,
      longitude: plannedSiteCoord.longitude,
    })
    const distanceMeters = km === undefined ? null : Math.round(km * 1000)
    if (
      distanceMeters !== null &&
      distanceMeters > FAR_FROM_PLANNED_THRESHOLD_M
    ) {
      warnedFarRef.current = true
      setFarDistanceMeters(distanceMeters)
    }
  }, [plannedSiteCoord, location, getDistanceKm])

  const onSubmit = useCallback(
    async (data: SiteFormDataInput) => {
      clearErrors("root")

      try {
        if (!user || !org) throw new Error("Not logged in")
        if (!projectId) throw new Error("Project ID is required")
        if (!siteType) throw new Error("Site type not found")

        // Try to back up staged bytes before saving, including a fresh capture
        // still inside the autosave debounce. Attachment writes remain awaited
        // even if draft storage is unavailable.
        await backupStagedPhotosForCreate(
          photoChanges.add.length,
          draft.flush,
          () =>
            toast.warning(
              "Photo backup unavailable. Keep this form open until saving finishes.",
            ),
        )

        const transformed = transformSurveySiteData(data)

        // Write to local SQLite via PowerSync, then navigate
        await createSurveySite.mutateAsync({
          ...transformed,
          id: siteIdRef.current,
          photos: photoChanges.add.map(
            (photo): PhotoInput => ({
              id: photo.id,
              org_id: org.id,
              caption: photo.caption,
              filename: photo.file.name,
              file_size: photo.file.size,
              mime_type: photo.file.type,
              file: photo.file,
            }),
          ),
        })

        // If recording from a planned-site task, link the new site back so
        // the task drops off "My tasks". This is a separate local write, so
        // it queues as its own CRUD transaction and uploads AFTER the
        // survey_site — keeping the planned_sites.survey_site_id FK
        // satisfied on the server. Don't block navigation on it: the site
        // is already saved, and a failed link can be retried or done by an
        // admin.
        if (plannedSite) {
          try {
            await linkSurveySiteToPlannedSite(plannedSite.id, siteIdRef.current)
          } catch (linkErr) {
            console.warn("Failed to link planned site to survey site:", linkErr)
          }
        }

        // Mark the originating assignment completed so it drops off "My
        // tasks" by status (not just by the survey_site_id link). Open
        // tasks have no assignment, so this only fires for assigned-to-me
        // tasks. Non-blocking: a failed status update still leaves the
        // task hidden via the link.
        if (assignmentId) {
          try {
            await completePlannedSiteAssignment(assignmentId)
          } catch (completeErr) {
            console.warn(
              "Failed to complete planned-site assignment:",
              completeErr,
            )
          }
        }

        // The site is in local storage now, so the draft has nothing left to
        // protect.
        await draft.discard()

        navigate({
          to: "/projects/$id/data/sites/$siteId",
          params: { id: projectId, siteId: siteIdRef.current },
        })
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Something went wrong while creating the site"
        setError("root", { message })
        posthog.capture("site_form_submit_failed", { message })
      }
    },
    [
      user,
      org,
      projectId,
      siteType,
      createSurveySite,
      photoChanges.add,
      navigate,
      setError,
      clearErrors,
      plannedSite,
      assignmentId,
      draft,
    ],
  )

  // Validation-blocked submit (as opposed to a thrown error in onSubmit):
  // capture the exact failing fields at the moment the user taps Create.
  // The data-entry event only fires on change, so without this a user who
  // hits a stuck-invalid form and stops editing produces no submit-time
  // signal.
  const onInvalid = useCallback((errors: FieldErrors<SiteFormDataInput>) => {
    captureFormSubmitBlocked("new_site", errors)
  }, [])

  const hasUnsavedChanges = useHasUnsavedChanges({
    form,
  })

  // Leaving only: FormExitGuard discards the draft first.
  const handleCancel = useCallback(() => {
    navigate({ to: "/projects/$id", params: { id: projectId } })
  }, [navigate, projectId])

  const handleSaveDraft = useCallback(
    () =>
      saveDraftForExit({
        validateIdentifier: () => form.trigger("name"),
        save: draft.saveForExit,
      }),
    [form, draft],
  )

  // Far-location warning: continue recording anyway, or leave the form.
  const handleFarContinue = useCallback(() => setFarDistanceMeters(null), [])
  const handleFarCancel = useCallback(() => {
    setFarDistanceMeters(null)
    void draft.discard()
    navigate({ to: "/projects/$id", params: { id: projectId } })
  }, [navigate, projectId, draft])

  const isGeometryValid = useCallback(
    (geometry: LocationShapes | undefined) => {
      if (!geometry) return true
      const parsed = schema.safeParse({
        location_type: siteLocationType,
        [location_property]: geometry,
      })
      if (parsed.success) return true
      return !(location_property in (parsed.error.flatten()?.fieldErrors ?? {}))
    },
    [location_property, siteLocationType, schema],
  )

  // Show whatever location error exists — the schema can fail on a
  // different location shape than the one being edited (see
  // getLocationError), so fall back across all of them.
  const locationError = getLocationError(errors, location_property)

  if (!user) return

  return (
    <div className="bg-background absolute inset-0 z-20 flex flex-col justify-between gap-2 pb-2">
      <div className="space-y-2 overflow-y-scroll">
        <div className="flex items-center p-2 text-2xl">
          <MapPin className="mr-2 size-6" />
          New {siteType.label}
        </div>

        <div className="flex flex-col gap-4 px-1">
          {/* Basic Fields */}
          <div className="space-y-1">
            <Label htmlFor="name">Site Name *</Label>
            <Input
              {...register("name")}
              id="name"
              className="h-12 text-lg"
              autoComplete="off"
              placeholder={`Enter ${siteType.label.toLowerCase()} name`}
            />
            {errors.name && (
              <div className="mt-1 text-sm text-red-600">
                {errors.name.message}
              </div>
            )}
          </div>

          {/* Location */}
          <div className="space-y-1">
            <Label htmlFor="location">
              Location ({siteType.site_location_type})
            </Label>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLocationSelector(true)}
              className="h-12 w-full justify-start text-lg"
            >
              <MapPin className="mr-2 size-5" />
              {watch(`location_${siteLocationType}`)
                ? "Location selected - tap to edit"
                : `Select ${siteLocationType} location`}
            </Button>
            {locationError && (
              <div className="mt-1 text-sm text-red-600">
                {String(locationError)}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              {...register("description")}
              id="description"
              className={`h-20 text-lg transition-all duration-500 ease-in-out ${
                descriptionFocus ? "h-40" : ""
              }`}
              placeholder="Enter site description or notes"
              onFocus={() => setDescriptionFocus(true)}
              onBlur={() => setDescriptionFocus(false)}
            />
          </div>

          {/* Recorders */}
          <RecordersSection control={control} user={user} />
          {errors.recorders && (
            <div className="text-sm text-red-600">
              {String(errors.recorders.message)}
            </div>
          )}

          {siteType.custom_fields.length > 0 && (
            <>
              <h3 className="text-lg font-semibold">Site Data</h3>
              <div className="space-y-2 md:columns-2 lg:columns-4">
                {siteType.custom_fields.map((field) => (
                  <div
                    key={`customFields.${field.name}`}
                    className="rounded-lg"
                  >
                    <div className="mb-1 flex flex-col items-center gap-2">
                      {getFormFieldComponent(
                        field,
                        register,
                        control,
                        siteContext,
                      )}
                      {field.type !== "subform" &&
                        errors.custom_fields?.[field.name]?.message && (
                          <span className="self-end text-orange-400">
                            {String(errors.custom_fields[field.name]?.message)}
                          </span>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Photos */}
          <PhotosForm
            entityType="survey_site"
            onCaptureStateChange={draft.setCaptureInFlight}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 border-t border-green-800 px-1 pt-2">
        {errors.root?.message && (
          <div className="rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-950">
            {errors.root.message}
          </div>
        )}
        <div className="flex flex-col gap-2 md:flex-row md:gap-4">
          <FormExitGuard
            hasUnsavedChanges={hasUnsavedChanges}
            discardDraft={draft.discard}
            onCancel={handleCancel}
            onSaveDraft={handleSaveDraft}
            isEditingDraft={Boolean(draftId && restoredDraft?.savedByUser)}
          />
          <Button
            type="submit"
            disabled={isSubmitting}
            className="h-12 w-full text-lg"
            onClick={handleSubmit(onSubmit, onInvalid)}
          >
            {isSubmitting ? "Creating..." : "Create Site"}
          </Button>
        </div>
      </div>

      {/* Location Selector Overlay */}
      {showLocationSelector && project && (
        <LocationSelector
          {...({
            isOpen: showLocationSelector,
            onClose: () => setShowLocationSelector(false),
            locationType: siteLocationType,
            project,
            value: watch(`location_${siteLocationType}`),
            onChange: (geometry: LocationShapes | undefined) =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setValue(`location_${siteLocationType}`, geometry as any, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              }),
            isValid: isGeometryValid,
            mapLayers: plannedSiteCoord ? (
              <PlannedSiteMarker
                latitude={plannedSiteCoord.latitude}
                longitude={plannedSiteCoord.longitude}
              />
            ) : undefined,
          } as LocationSelectorProps)}
        />
      )}

      {/* Distance confirmation: recording from a planned-site task while far
          from the planned location. */}
      <AlertDialog open={farDistanceMeters !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Distance from planned site warning
            </AlertDialogTitle>
            <AlertDialogDescription className="flex flex-col gap-2 text-base">
              You're currently more than{" "}
              {farDistanceMeters?.toLocaleString() ?? 0} meters from the planned
              location for site {plannedSite?.name?.trim() || "Unnamed"}. Are
              you sure you wish to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="cursor-pointer"
              onClick={handleFarCancel}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={handleFarContinue}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
